/**
 * D1 caps a prepared statement at 100 bound parameters. Every remote (worker-proxied) database
 * read funnels through drizzle's `sqlite-proxy` `RemoteCallback`, so a single `where col in (...)`
 * over a few thousand graphemes — routine on the Browse Levels / Characters / Deck screens, which
 * fetch catalog facts for a whole grid in one set-based read — blows the cap and the worker fails
 * the request with `D1_ERROR: too many SQL variables`. Native is unaffected (local SQLite allows
 * ~32k variables), so this is a web/server-transport-only concern.
 *
 * Rather than chunking at each call site (easy to forget, and there are many), this module makes
 * the transport itself split an oversized statement into several under-cap statements and
 * concatenate the row sets.
 *
 * SAFETY: concatenating chunk results is only equivalent to the original query for a query whose
 * result is the UNION of its `IN`-list rows. That is emphatically not true in general — an
 * aggregate (`count(*)`), a `LIMIT`, a `GROUP BY`, `DISTINCT`, or a window function all produce a
 * result that cannot be reassembled from partial runs. So {@link planWorkerQueryParamChunks}
 * refuses to split unless it can prove the shape is safe, and returns `null` otherwise. A `null`
 * plan means "send it unsplit" — the query may still fail against D1, but it fails loudly with the
 * real SQL error instead of silently returning wrong data. Correctness beats coverage here.
 */

/**
 * D1's hard limit on bound parameters per statement. Verified empirically against the dev worker:
 * 100 params → 200 OK, 101 → 500 `too many SQL variables`.
 */
import { hasLength, hasMinLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

export const D1_MAX_BOUND_PARAMS = 100;

/**
 * Rows-returning methods whose result sets concatenate cleanly. `get` returns a single row (the
 * caller wants one match, and merging chunk results would have to re-pick a winner) and `run`
 * returns no rows, so neither is split.
 */
const CHUNKABLE_METHODS = new Set(["all", "values"]);

/**
 * Constructs whose presence means the row set is NOT the plain union of the `IN` list's matches.
 * Matched against the SQL with string literals stripped, so a table/column named e.g. `summary`
 * can't trip the `sum(` check via a literal. Deliberately broad: a false positive costs us a
 * split (the query is sent whole and may fail loudly), while a false negative silently corrupts
 * results.
 */
const UNSAFE_SQL_RE =
  /\b(?:limit|offset|group\s+by|having|distinct|union|intersect|except|order\s+by)\b|\b(?:count|sum|avg|min|max|total|group_concat|row_number|rank|dense_rank|ntile|lag|lead|first_value|last_value)\s*\(|\bover\s*\(/i;

/**
 * Blank out everything nested inside parentheses, keeping the string's length so offsets stay
 * valid. Only the OUTER query's shape decides whether chunk results concatenate: drizzle's RQB-v2
 * builds a relation (`with: { prcData: true }`) as a correlated subquery carrying its own
 * `limit ?`, and an `IN` list in a `where` is itself parenthesised. Those nested constructs are
 * per-row and don't reshape the outer row set, so scanning the raw SQL for `limit` would refuse
 * to split exactly the reads that need it most.
 */
function maskParenthesisedGroups(sql: string): string {
  let masked = "";
  let depth = 0;
  // Iterating the string yields code points, which is safe here: we only compare against the ASCII
  // parens `(`/`)` and re-emit every other character unchanged, so a multi-code-unit glyph is
  // preserved verbatim rather than decomposed into parts we'd act on.
  for (const char of sql) {
    if (char === "(") {
      depth += 1;
      masked += char;
      continue;
    }
    if (char === ")") {
      depth = Math.max(0, depth - 1);
      masked += char;
      continue;
    }
    masked += depth > 0 ? " " : char;
  }
  return masked;
}

/** A single placeholder run: `(?, ?, ?)` — i.e. the parameter list of one `IN` clause. */
interface PlaceholderRun {
  /** Number of `?` placeholders in the run. */
  count: number;
  /** Index into the SQL string just past the run's final `?` (before `)`). */
  end: number;
  /** Index into the SQL string where the run's placeholders begin (just after `(`). */
  start: number;
}

/** Matches a parenthesised run of two or more comma-separated placeholders: `(?, ?, ...)`. */
const PLACEHOLDER_RUN_RE = /\(\s*\?(?:\s*,\s*\?)+\s*\)/g;

/**
 * Replace every single-quoted SQL string literal with same-length blanks so literal text can't be
 * mistaken for a keyword or a placeholder. Length is preserved so the offsets we compute against
 * the masked string stay valid against the original.
 */
function maskStringLiterals(sql: string): string {
  return sql.replaceAll(/'(?:[^']|'')*'/g, (literal) => " ".repeat(literal.length));
}

/** A plan to execute one oversized statement as several under-cap statements. */
export interface WorkerQueryChunkPlan {
  /** The per-chunk statements to run, in order. Their concatenated rows equal the original's. */
  chunks: { params: unknown[]; sql: string }[];
}

/**
 * Decide whether `sql`/`params` can be split into statements of at most {@link D1_MAX_BOUND_PARAMS}
 * bound parameters each, and if so how.
 *
 * Returns `null` (meaning "do not split; send as-is") when the statement is already under the cap,
 * when the method isn't row-concatenable, or when the SQL shape makes concatenation unsound. The
 * only shape accepted is: a rows-returning statement with exactly ONE multi-placeholder run, where
 * that run accounts for every bound parameter beyond a fixed prefix/suffix that each chunk can
 * safely repeat, and with no result-reshaping construct anywhere in the statement.
 */
export function planWorkerQueryParamChunks(
  sql: string,
  params: readonly unknown[],
  method: string,
): WorkerQueryChunkPlan | null {
  if (!hasMinLength(params, D1_MAX_BOUND_PARAMS + 1) || !CHUNKABLE_METHODS.has(method)) {
    return null;
  }

  const maskedSql = maskStringLiterals(sql);
  // Safety is judged on the OUTER query only — a nested subquery's own `limit`/aggregate shapes
  // that subquery's per-row value, not the row set we're concatenating.
  if (UNSAFE_SQL_RE.test(maskParenthesisedGroups(maskedSql))) {
    return null;
  }

  // Every `?` in the statement, so we can confirm the big run is the only source of the overflow
  // and locate the run's parameters within the flat `params` array.
  const totalPlaceholders = (maskedSql.match(/\?/g) ?? []).length;
  if (!hasLength(params, totalPlaceholders)) {
    // Numbered/named parameters (`?1`, `:name`) or a placeholder count we can't reconcile — the
    // positional mapping below wouldn't hold.
    return null;
  }

  const runs: PlaceholderRun[] = [];
  for (const match of maskedSql.matchAll(PLACEHOLDER_RUN_RE)) {
    const text = match[0];
    const openParen = match.index;
    runs.push({
      count: (text.match(/\?/g) ?? []).length,
      end: openParen + text.lastIndexOf("?") + 1,
      start: openParen + text.indexOf("?"),
    });
  }
  if (!hasLength(runs, 1)) {
    // Zero runs: the overflow isn't an `IN` list we can split. More than one: splitting one run
    // while repeating another changes the cross product; not worth the risk.
    return null;
  }

  const [run] = runs;
  if (run === undefined) {
    return null;
  }

  // Parameters before/after the run are fixed bindings (e.g. a `where x = ?` alongside the `IN`).
  // Each chunk repeats them verbatim, so they must leave room under the cap.
  const paramsBefore = (maskedSql.slice(0, run.start).match(/\?/g) ?? []).length;
  const fixedParamCount = arrayLength(params) - run.count;
  const maxRunParamsPerChunk = D1_MAX_BOUND_PARAMS - fixedParamCount;
  if (maxRunParamsPerChunk < 1) {
    return null;
  }

  const prefixParams = params.slice(0, paramsBefore);
  const runParams = params.slice(paramsBefore, paramsBefore + run.count);
  const suffixParams = params.slice(paramsBefore + run.count);
  const sqlBeforeRun = sql.slice(0, run.start);
  const sqlAfterRun = sql.slice(run.end);

  const chunks: { params: unknown[]; sql: string }[] = [];
  for (let offset = 0; offset < arrayLength(runParams); offset += maxRunParamsPerChunk) {
    const chunkRunParams = runParams.slice(offset, offset + maxRunParamsPerChunk);
    chunks.push({
      params: [...prefixParams, ...chunkRunParams, ...suffixParams],
      sql:
        sqlBeforeRun +
        Array.from({ length: arrayLength(chunkRunParams) })
          .fill("?")
          .join(", ") +
        sqlAfterRun,
    });
  }
  return { chunks };
}
